import katex from "katex";
import "katex/dist/katex.min.css";
import { FC, useState } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type QuizQuestion = {
    q: string;
    options: string[];
    answer: number; // 0-based index of correct option
};

// ─── Math rendering ─────────────────────────────────────────────────────────
// Renders inline \( ... \) and display \[ ... \] LaTeX, leaves the rest as text.

const renderMath = (str: string): { __html: string } => {
    try {
        let out = (str ?? "")
            // display math \[ ... \]
            .replace(/\\\[([\s\S]+?)\\\]/g, (m, tex) => {
                try {
                    return katex.renderToString(tex, { displayMode: true, throwOnError: false });
                } catch {
                    return m;
                }
            })
            // inline math \( ... \)
            .replace(/\\\(([\s\S]+?)\\\)/g, (m, tex) => {
                try {
                    return katex.renderToString(tex, { displayMode: false, throwOnError: false });
                } catch {
                    return m;
                }
            });
        return { __html: out };
    } catch {
        return { __html: str ?? "" };
    }
};

// ─── Component ────────────────────────────────────────────────────────────────

type Props = {
    questions: QuizQuestion[];
};

const QuizInline: FC<Props> = ({ questions }) => {
    const [current, setCurrent] = useState(0);
    const [selected, setSelected] = useState<(number | null)[]>(
        new Array(questions.length).fill(null)
    );
    const [showResults, setShowResults] = useState(false);

    if (!questions || questions.length === 0) return null;

    const total = questions.length;
    const q = questions[current];

    // ── Results screen ──────────────────────────────────────────────────────────

    if (showResults) {
        const correct = questions.filter((q, i) => selected[i] === q.answer).length;

        return (
            <div style={styles.card}>
                {/* Score header */}
                <div style={styles.resultsScore}>
                    {correct} / {total}
                    <span style={{ ...styles.scoreBadge, background: correct >= total / 2 ? "#e6f4ea" : "#fdecea", color: correct >= total / 2 ? "#2e7d32" : "#c62828" }}>
                        {correct === total ? "Parfait !" : correct >= total / 2 ? "Bon travail." : "Continue à réviser."}
                    </span>
                </div>

                {/* Results table */}
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={{ ...styles.th, textAlign: "left" }}>Question</th>
                            <th style={{ ...styles.th, textAlign: "left" }}>Ta réponse</th>
                            <th style={{ ...styles.th, textAlign: "left" }}>Correction</th>
                        </tr>
                    </thead>
                    <tbody>
                        {questions.map((q, i) => {
                            const ok = selected[i] === q.answer;
                            const userAns = selected[i] !== null ? q.options[selected[i]!] : "—";
                            const correctAns = q.options[q.answer];
                            return (
                                <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                    <td style={{ ...styles.td, width: 28 }}>
                                        <span style={ok ? styles.badgeOk : styles.badgeKo}>
                                            {ok ? "✓" : "✗"}
                                        </span>
                                    </td>
                                    <td style={styles.td} dangerouslySetInnerHTML={renderMath(q.q)} />
                                    <td style={{ ...styles.td, color: ok ? "#2e7d32" : "#c62828" }} dangerouslySetInnerHTML={renderMath(userAns)} />
                                    <td style={{ ...styles.td, color: "#888" }} dangerouslySetInnerHTML={renderMath(ok ? "" : correctAns)} />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Restart */}
                <button
                    style={styles.restartBtn}
                    onClick={() => {
                        setCurrent(0);
                        setSelected(new Array(questions.length).fill(null));
                        setShowResults(false);
                    }}
                >
                    Recommencer
                </button>
            </div>
        );
    }

    // ── Quiz screen ─────────────────────────────────────────────────────────────

    const isLast = current === total - 1;
    const allAnswered = selected.every((s) => s !== null);

    return (
        <div style={styles.card}>
            {/* Header */}
            <div style={styles.header}>
                <span style={styles.progress}>{current + 1} sur {total}</span>
            </div>

            {/* Question */}
            <div style={styles.question} dangerouslySetInnerHTML={renderMath(q.q)} />

            {/* Options */}
            <div>
                {q.options.map((opt, i) => {
                    const isSelected = selected[current] === i;
                    return (
                        <div
                            key={i}
                            style={{
                                ...styles.optionRow,
                                background: isSelected ? "#f5f5f5" : "#fff",
                                borderTop: i === 0 ? "1px solid #f0f0f0" : "1px solid #f0f0f0",
                            }}
                            onClick={() => {
                                const updated = [...selected];
                                updated[current] = i;
                                setSelected(updated);
                            }}
                        >
                            <div style={{ ...styles.badge, background: isSelected ? "#393E3E" : "#f5f5f5", color: isSelected ? "#fff" : "#888" }}>
                                {i + 1}
                            </div>
                            <div style={styles.optionText} dangerouslySetInnerHTML={renderMath(opt)} />
                            {isSelected && <div style={styles.arrow}>→</div>}
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div style={styles.footer}>
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        style={styles.navBtn}
                        disabled={current === 0}
                        onClick={() => setCurrent((c) => c - 1)}
                    >
                        ← Précédent
                    </button>
                    {!isLast && (
                        <button style={styles.navBtn} onClick={() => setCurrent((c) => c + 1)}>
                            Suivant →
                        </button>
                    )}
                </div>
                {(isLast || allAnswered) && (
                    <button
                        style={{ ...styles.navBtn, ...styles.primaryBtn }}
                        onClick={() => setShowResults(true)}
                    >
                        Voir les résultats
                    </button>
                )}
            </div>
        </div>
    );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    card: {
        marginTop: 12,
        border: "1px solid #EAEAEA",
        borderRadius: 12,
        overflow: "hidden",
        background: "#fff",
        fontSize: 14,
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderBottom: "1px solid #f0f0f0",
    },
    progress: {
        fontSize: 12,
        color: "#888",
    },
    closeBtn: {
        background: "none",
        border: "none",
        fontSize: 18,
        color: "#888",
        cursor: "pointer",
        lineHeight: 1,
        padding: 0,
    },
    question: {
        padding: "14px 14px 10px",
        fontWeight: 500,
        fontSize: 14,
        lineHeight: 1.5,
        color: "#1a1a1a",
    },
    optionRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        cursor: "pointer",
        transition: "background 0.1s",
    },
    badge: {
        width: 24,
        height: 24,
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 500,
        flexShrink: 0,
    },
    optionText: {
        flex: 1,
        fontSize: 13,
        color: "#1a1a1a",
        lineHeight: 1.4,
    },
    arrow: {
        fontSize: 13,
        color: "#888",
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderTop: "1px solid #f0f0f0",
    },
    navBtn: {
        background: "none",
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        padding: "5px 12px",
        fontSize: 12,
        color: "#1a1a1a",
        cursor: "pointer",
    },
    primaryBtn: {
        background: "#393E3E",
        color: "#fff",
        border: "none",
    },
    resultsScore: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "16px 14px 10px",
        fontSize: 20,
        fontWeight: 500,
        color: "#1a1a1a",
    },
    scoreBadge: {
        fontSize: 12,
        fontWeight: 400,
        padding: "3px 10px",
        borderRadius: 20,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 12,
        padding: "0 14px",
    },
    th: {
        padding: "6px 8px",
        color: "#888",
        fontWeight: 400,
        borderBottom: "1px solid #f0f0f0",
    },
    td: {
        padding: "8px 8px",
        color: "#1a1a1a",
        verticalAlign: "top",
        lineHeight: 1.4,
    },
    badgeOk: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#e6f4ea",
        color: "#2e7d32",
        fontSize: 10,
        fontWeight: 600,
    },
    badgeKo: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#fdecea",
        color: "#c62828",
        fontSize: 10,
        fontWeight: 600,
    },
    restartBtn: {
        margin: "12px 14px",
        background: "none",
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        padding: "6px 14px",
        fontSize: 12,
        color: "#1a1a1a",
        cursor: "pointer",
    },
};

export default QuizInline;